// frontend/src/App.jsx
import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './AuthContext';
import Login from './Login';
import LandingPage from './LandingPage';
import TodayPage from './TodayPage';
import PortfolioPage from './PortfolioPage';
import OnboardingPage from './OnboardingPage';
import BotsPage from './BotsPage';
import BotReportPage from './BotReportPage';
import WatchAnalysisPage from './WatchAnalysisPage';
import SettingsPage from './SettingsPage';
import StockExplorer from './StockExplorer';
import StockExplorerDetail from './StockExplorerDetail';
import AccountSettings from './components/AccountSettings';
import Terms from './Terms';
import Privacy from './Privacy';
import Help from './HelpPage';
import Subscription from './Subscription';
import LiveLog from './pages/LiveLog';
import Signup from './components/Signup';
import NotFoundPage from './components/NotFoundPage';
import AdminDashboard from './AdminDashboard'; // Add import
import AdminPublicDisclosurePreview from './AdminPublicDisclosurePreview';
import Enable2FA from './Enable2FA';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import BacktestsPage from './BacktestsPage';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';

const queryClient = new QueryClient();

const App = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router>
          <AuthProvider>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/help" element={<Help />} />
              <Route path="/subscribe" element={<Subscription />} />
              <Route path="/enable-2fa" element={<Enable2FA />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <TodayPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/portfolio" element={<ProtectedRoute><PortfolioPage /></ProtectedRoute>} />
              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute>
                    <OnboardingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bots"
                element={
                  <ProtectedRoute>
                    <BotsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bots/:botId/report"
                element={<ProtectedRoute><BotReportPage /></ProtectedRoute>}
              />
              <Route path="/attention/:attentionId" element={<ProtectedRoute><WatchAnalysisPage /></ProtectedRoute>} />
              <Route
                path="/backtests"
                element={<ProtectedRoute><BacktestsPage /></ProtectedRoute>}
              />
              <Route
                path="/backtests/:jobId"
                element={<ProtectedRoute><BacktestsPage /></ProtectedRoute>}
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/stock-explorer"
                element={
                  <ProtectedRoute>
                    <StockExplorer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/stock-explorer/instruments/:instrumentId"
                element={<ProtectedRoute><StockExplorerDetail /></ProtectedRoute>}
              />
              <Route
                path="/account-settings"
                element={
                  <ProtectedRoute>
                    <AccountSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/live-log"
                element={
                  <ProtectedRoute>
                    <LiveLog />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/public-disclosures/preview"
                element={
                  <ProtectedRoute>
                    <AdminPublicDisclosurePreview />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </AuthProvider>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
